import React, { useState } from 'react';

const ScientificCalculator = () => {
  const [display, setDisplay] = useState('0');
  const [memory, setMemory] = useState(0);
  const [lastAnswer, setLastAnswer] = useState(0);
  const [isRadians, setIsRadians] = useState(false);
  const [waitingForOperand, setWaitingForOperand] = useState(true);
  const [pendingOperator, setPendingOperator] = useState(null);
  const [storedValue, setStoredValue] = useState(null);

  const clearAll = () => {
    setDisplay('0');
    setWaitingForOperand(true);
    setPendingOperator(null);
    setStoredValue(null);
  };

  const inputDigit = (digit) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? digit : display + digit);
    }
  };

  const inputDot = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const toggleSign = () => {
    const value = parseFloat(display);
    setDisplay(String(-value));
  };

  const inputPercent = () => {
    const value = parseFloat(display);
    setDisplay(String(value / 100));
  };

  const performOperation = (operator) => {
    const inputValue = parseFloat(display);

    if (storedValue === null) {
      setStoredValue(inputValue);
    } else if (pendingOperator) {
      const result = calculate(storedValue, inputValue, pendingOperator);
      setDisplay(String(result));
      setStoredValue(result);
    }

    setWaitingForOperand(true);
    setPendingOperator(operator);
  };

  const calculate = (left, right, op) => {
    switch (op) {
      case '+': return left + right;
      case '-': return left - right;
      case '×': return left * right;
      case '/': return right !== 0 ? left / right : 'Error';
      case 'xy': return Math.pow(left, right);
      case 'y√x': return Math.pow(left, 1 / right);
      default: return right;
    }
  };

  const performEquals = () => {
    const inputValue = parseFloat(display);
    if (pendingOperator && storedValue !== null) {
      const result = calculate(storedValue, inputValue, pendingOperator);
      setDisplay(String(result));
      setLastAnswer(result);
      setStoredValue(null);
      setPendingOperator(null);
      setWaitingForOperand(true);
    }
  };

  const scientificFunction = (func) => {
    const value = parseFloat(display);
    let result;
    const angle = isRadians ? value : (value * Math.PI) / 180;

    switch (func) {
      case 'sin': result = Math.sin(angle); break;
      case 'cos': result = Math.cos(angle); break;
      case 'tan': result = Math.tan(angle); break;
      case 'sin-1': result = isRadians ? Math.asin(value) : (Math.asin(value) * 180) / Math.PI; break;
      case 'cos-1': result = isRadians ? Math.acos(value) : (Math.acos(value) * 180) / Math.PI; break;
      case 'tan-1': result = isRadians ? Math.atan(value) : (Math.atan(value) * 180) / Math.PI; break;
      case 'ln': result = Math.log(value); break;
      case 'log': result = Math.log10(value); break;
      case 'x2': result = value * value; break;
      case 'x3': result = value * value * value; break;
      case '√x': result = Math.sqrt(value); break;
      case '3√x': result = Math.cbrt(value); break;
      case 'ex': result = Math.exp(value); break;
      case '10x': result = Math.pow(10, value); break;
      case '1/x': result = 1 / value; break;
      case 'n!': result = factorial(Math.floor(value)); break;
      default: result = value;
    }

    setDisplay(String(result));
    setWaitingForOperand(true);
  };

  const factorial = (n) => {
    if (n < 0) return NaN;
    if (n === 0 || n === 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) result *= i;
    return result;
  };

  const insertConstant = (constant) => {
    if (constant === 'π') setDisplay(String(Math.PI));
    else if (constant === 'e') setDisplay(String(Math.E));
    setWaitingForOperand(true);
  };

  const randomNumber = () => {
    setDisplay(String(Math.random()));
    setWaitingForOperand(true);
  };

  const backspace = () => {
    if (display.length > 1) {
      setDisplay(display.slice(0, -1));
    } else {
      setDisplay('0');
      setWaitingForOperand(true);
    }
  };

  const memoryAdd = () => setMemory(memory + parseFloat(display));
  const memorySubtract = () => setMemory(memory - parseFloat(display));
  const memoryRecall = () => {
    setDisplay(String(memory));
    setWaitingForOperand(true);
  };
  const answerRecall = () => {
    setDisplay(String(lastAnswer));
    setWaitingForOperand(true);
  };

  const insertEXP = () => {
    if (!display.includes('e')) {
      setDisplay(display + 'e');
      setWaitingForOperand(false);
    }
  };

  const ScientificButton = ({ label, onClick, className = '' }) => (
    <button
      onClick={onClick}
      className={`h-9 text-xs font-medium rounded-lg transition-all duration-150 hover:brightness-125 active:scale-95 ${className}`}
    >
      {label}
    </button>
  );

  const NumberButton = ({ label, onClick, className = '' }) => (
    <button
      onClick={onClick}
      className={`h-11 text-sm font-semibold rounded-lg transition-all duration-150 hover:brightness-125 active:scale-95 ${className}`}
    >
      {label}
    </button>
  );

  return (
    <div className="bg-[#1a1a1a] rounded-2xl shadow-2xl p-4 w-full max-w-[440px] border border-[#2a2a2a]">
      {/* Display */}
      <div className="bg-[#0d0d0d] rounded-xl mb-4 p-4 text-right border border-[#2a2a2a]">
        <div className="text-cyan-400 text-3xl font-mono overflow-hidden text-ellipsis">
          {display}
        </div>
      </div>

      <div className="flex gap-2">
        {/* Scientific Functions Column */}
        <div className="flex flex-col gap-1.5 w-[45%]">
          <div className="grid grid-cols-5 gap-1">
            <ScientificButton label="sin" onClick={() => scientificFunction('sin')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="cos" onClick={() => scientificFunction('cos')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="tan" onClick={() => scientificFunction('tan')} className="bg-[#2a2a2a] text-gray-300" />
            <button
              onClick={() => setIsRadians(false)}
              className={`h-9 text-xs font-medium rounded-lg transition-all ${!isRadians ? 'bg-cyan-600 text-white' : 'bg-[#2a2a2a] text-gray-400'}`}
            >
              Deg
            </button>
            <button
              onClick={() => setIsRadians(true)}
              className={`h-9 text-xs font-medium rounded-lg transition-all ${isRadians ? 'bg-cyan-600 text-white' : 'bg-[#2a2a2a] text-gray-400'}`}
            >
              Rad
            </button>
          </div>

          <div className="grid grid-cols-5 gap-1">
            <ScientificButton label="sin⁻¹" onClick={() => scientificFunction('sin-1')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="cos⁻¹" onClick={() => scientificFunction('cos-1')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="tan⁻¹" onClick={() => scientificFunction('tan-1')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="π" onClick={() => insertConstant('π')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="e" onClick={() => insertConstant('e')} className="bg-[#2a2a2a] text-gray-300" />
          </div>

          <div className="grid grid-cols-5 gap-1">
            <ScientificButton label="xʸ" onClick={() => performOperation('xy')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="x³" onClick={() => scientificFunction('x3')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="x²" onClick={() => scientificFunction('x2')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="eˣ" onClick={() => scientificFunction('ex')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="10ˣ" onClick={() => scientificFunction('10x')} className="bg-[#2a2a2a] text-gray-300" />
          </div>

          <div className="grid grid-cols-5 gap-1">
            <ScientificButton label="ʸ√x" onClick={() => performOperation('y√x')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="³√x" onClick={() => scientificFunction('3√x')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="√x" onClick={() => scientificFunction('√x')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="ln" onClick={() => scientificFunction('ln')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="log" onClick={() => scientificFunction('log')} className="bg-[#2a2a2a] text-gray-300" />
          </div>

          <div className="grid grid-cols-5 gap-1">
            <ScientificButton label="(" onClick={() => {}} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label=")" onClick={() => {}} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="1/x" onClick={() => scientificFunction('1/x')} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="%" onClick={inputPercent} className="bg-[#2a2a2a] text-gray-300" />
            <ScientificButton label="n!" onClick={() => scientificFunction('n!')} className="bg-[#2a2a2a] text-gray-300" />
          </div>
        </div>

        {/* Number Pad Column */}
        <div className="flex flex-col gap-1.5 w-[55%]">
          <div className="grid grid-cols-5 gap-1">
            <NumberButton label="7" onClick={() => inputDigit('7')} className="bg-[#3a3a3a] text-white" />
            <NumberButton label="8" onClick={() => inputDigit('8')} className="bg-[#3a3a3a] text-white" />
            <NumberButton label="9" onClick={() => inputDigit('9')} className="bg-[#3a3a3a] text-white" />
            <NumberButton label="+" onClick={() => performOperation('+')} className="bg-cyan-600 text-white" />
            <NumberButton label="Back" onClick={backspace} className="bg-[#2a2a2a] text-gray-300 text-xs" />
          </div>

          <div className="grid grid-cols-5 gap-1">
            <NumberButton label="4" onClick={() => inputDigit('4')} className="bg-[#3a3a3a] text-white" />
            <NumberButton label="5" onClick={() => inputDigit('5')} className="bg-[#3a3a3a] text-white" />
            <NumberButton label="6" onClick={() => inputDigit('6')} className="bg-[#3a3a3a] text-white" />
            <NumberButton label="−" onClick={() => performOperation('-')} className="bg-cyan-600 text-white" />
            <NumberButton label="Ans" onClick={answerRecall} className="bg-[#2a2a2a] text-gray-300 text-xs" />
          </div>

          <div className="grid grid-cols-5 gap-1">
            <NumberButton label="1" onClick={() => inputDigit('1')} className="bg-[#3a3a3a] text-white" />
            <NumberButton label="2" onClick={() => inputDigit('2')} className="bg-[#3a3a3a] text-white" />
            <NumberButton label="3" onClick={() => inputDigit('3')} className="bg-[#3a3a3a] text-white" />
            <NumberButton label="×" onClick={() => performOperation('×')} className="bg-cyan-600 text-white" />
            <NumberButton label="M+" onClick={memoryAdd} className="bg-[#2a2a2a] text-gray-300 text-xs" />
          </div>

          <div className="grid grid-cols-5 gap-1">
            <NumberButton label="0" onClick={() => inputDigit('0')} className="bg-[#3a3a3a] text-white" />
            <NumberButton label="." onClick={inputDot} className="bg-[#3a3a3a] text-white" />
            <NumberButton label="EXP" onClick={insertEXP} className="bg-[#3a3a3a] text-white text-xs" />
            <NumberButton label="÷" onClick={() => performOperation('/')} className="bg-cyan-600 text-white" />
            <NumberButton label="M−" onClick={memorySubtract} className="bg-[#2a2a2a] text-gray-300 text-xs" />
          </div>

          <div className="grid grid-cols-5 gap-1">
            <NumberButton label="±" onClick={toggleSign} className="bg-[#3a3a3a] text-white" />
            <NumberButton label="RND" onClick={randomNumber} className="bg-[#3a3a3a] text-white text-xs" />
            <NumberButton label="AC" onClick={clearAll} className="bg-red-500 text-white" />
            <NumberButton label="=" onClick={performEquals} className="bg-green-500 text-white" />
            <NumberButton label="MR" onClick={memoryRecall} className="bg-[#2a2a2a] text-gray-300 text-xs" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScientificCalculator;
